import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export interface MemberPet {
  pet_id: string;
  member_id: string | null;
  pet_type_id: string;
  pet_type_name?: string | null;
  name: string;
  breed?: string | null;
  colour?: string | null;
  gender?: string | null;
  photo_url?: string | null;
  property_location_id?: string | null;
  created_at?: string;
}

export interface PetTypeOption {
  pet_type_id: string;
  name: string;
}

export interface PetFieldPolicy {
  module_key: string;
  entity_key: string;
  field_key: string;
  label: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'fk' | 'enum';
  system_required: boolean;
  is_required: boolean;
  is_visible: boolean;
  default_value: string | null;
  is_tenant_overridden: boolean;
  description?: string;
}

export interface PetPayload {
  pet_type_id: string;
  name: string;
  breed?: string;
  colour?: string;
  gender?: string;
  property_location_id?: string | null;
}

export async function fetchMemberPets(memberId: string): Promise<MemberPet[]> {
  const { data } = await tenantApi.get(`/members/${memberId}/pets`, {
    params: { limit: 100, sort_by: 'name', sort_order: 'asc' },
  });
  return unwrapList<MemberPet>(data);
}

export async function fetchActivePetTypes(): Promise<PetTypeOption[]> {
  const { data } = await tenantApi.get('/pet-types/active');
  return unwrapList<PetTypeOption>(data);
}

export async function fetchMemberPetFieldPolicy(memberId: string): Promise<PetFieldPolicy[]> {
  const { data } = await tenantApi.get(`/members/${memberId}/pets/policy`);
  return unwrapList<PetFieldPolicy>(data);
}

export async function createPet(memberId: string, payload: PetPayload): Promise<MemberPet> {
  const { data } = await tenantApi.post(`/members/${memberId}/pets`, payload);
  return unwrap<MemberPet>(data);
}

export async function updatePet(
  memberId: string,
  petId: string,
  payload: Partial<PetPayload>,
): Promise<MemberPet> {
  const { data } = await tenantApi.put(`/members/${memberId}/pets/${petId}`, payload);
  return unwrap<MemberPet>(data);
}

export async function uploadPetPhoto(
  memberId: string,
  petId: string,
  file: Blob,
): Promise<string | undefined> {
  const ext = file.type === 'image/png' ? 'png' : 'jpg';
  const formData = new FormData();
  formData.append('file', file, file instanceof File ? file.name : `pet.${ext}`);
  const { data } = await tenantApi.post(
    `/members/${memberId}/pets/${petId}/photo`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return unwrap<{ photo_url?: string }>(data).photo_url;
}

export async function deletePet(memberId: string, petId: string): Promise<void> {
  await tenantApi.delete(`/members/${memberId}/pets/${petId}`);
}
